import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { RequestModel } from "model";
import { Link } from "react-router-dom";
import { createSubProfilePath } from "routes/routeData";
import { Button, Card, DataItemView, Icon, useFeatureFlags } from "swing-components";

import { releasePriorityAccess, RESTRICTIONS, translate, useShouldRestrictAdmin } from "~utils";

/********* Types ********/
type PriorityAccessDetailsProps = {
  request: RequestModel;
};

/********* Component ********/
export function PriorityAccessDetails(props: PriorityAccessDetailsProps) {
  const { request } = props;
  const subs = request.priorityAccessSubs;
  const { flags } = useFeatureFlags();

  const [showSubs, setShowSubs] = useState(false);

  const queryClient = useQueryClient();
  const isViewOnly = useShouldRestrictAdmin([
    RESTRICTIONS.VIEW_ONLY_ADMIN,
    RESTRICTIONS.CLASSROOM_VIEW_ONLY,
  ]);

  const { mutate: postReleasePriorityAccess, isPending: isReleasing } = useMutation({
    mutationFn: () => releasePriorityAccess(request.id),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    },
  });

  if (!subs.length) {
    return null;
  }

  return (
    <Card>
      <h3 css={{ margin: "0 0 var(--spacing-lg) 0" }}>{translate("LABEL_PRIORITY_ACCESS")}</h3>

      <DataItemView
        icon="Star"
        label={translate("LABEL_PRIORITY_ACCESS_SUBS_SELECTED", { count: subs.length })}
        style={{ marginBottom: "var(--spacing-lg)" }}
      >
        <div>
          <button
            type="button"
            onClick={() => setShowSubs((prev) => !prev)}
            css={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontWeight: 700,
              color: "var(--swing-blue500)",
            }}
          >
            {showSubs
              ? translate("LABEL_PRIORITY_ACCESS_HIDE_SUBS")
              : translate("LABEL_PRIORITY_ACCESS_SHOW_SUBS")}
            <Icon name={showSubs ? "Chevron Up" : "Chevron Down"} />
          </button>

          {showSubs && (
            <ul css={{ margin: "var(--spacing-sm) 0 0", paddingLeft: "var(--spacing-lg)" }}>
              {subs.map((sub) => (
                <Link key={sub.id} to={createSubProfilePath("active-requests", sub.id)}>
                  <li>{sub.name}</li>
                </Link>
              ))}
            </ul>
          )}
        </div>
      </DataItemView>

      {/* Open time */}
      <DataItemView icon="Clock" label={renderOpenTimeLabel(request.priorityAccessOpenTime)}>
        {renderOpenTime(request.priorityAccessOpenTime, request.school.tzString)}
      </DataItemView>

      {!isViewOnly && canRelease(request.priorityAccessOpenTime) && flags.allowGpRelease && (
        <div css={{ marginTop: "var(--spacing-lg)" }}>
          <Button
            onClick={() => postReleasePriorityAccess()}
            status={isReleasing ? "loading" : "enabled"}
          >
            {translate("LABEL_PRIORITY_ACCESS_RELEASE")}
          </Button>
        </div>
      )}
    </Card>
  );
}

/********* Helper Functions ********/

/**
 * The server writes an open time ~5 years out for requests only preferred subs
 * can access. Anything a year out or more should be a reasonable check that only
 * preferred can sign up.
 */
function renderOpenTime(openTime: DateTime | undefined, tzString: string): string {
  const oneYearFromNow = DateTime.now().plus({ years: 1 });

  if (!openTime || openTime > oneYearFromNow) {
    return translate("LABEL_PRIORITY_ACCESS_OPEN_NEVER");
  }

  return openTime.setZone(tzString).toFormat("EEE, MMM d 'at' h:mm a");
}

/**
 * A request can be released until it opens to more subs. Requests only preferred
 * subs can access have no open time and can always be released.
 */
function canRelease(openTime: DateTime | undefined): boolean {
  return !openTime || openTime > DateTime.now();
}

function renderOpenTimeLabel(openTime: DateTime | undefined): string | undefined {
  const oneYearFromNow = DateTime.now().plus({ years: 1 });

  if (!openTime || openTime > oneYearFromNow) {
    return translate("LABEL_PRIORITY_ACCESS_OPENED_FUTURE");
  }

  return openTime > DateTime.now()
    ? translate("LABEL_PRIORITY_ACCESS_OPENED_FUTURE")
    : translate("LABEL_PRIORITY_ACCESS_OPENED_PAST");
}
